import { useEffect, useMemo } from 'react';
import { Controller, useForm, type Resolver } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Switch,
  TextField,
} from '@mui/material';
import { MdInfoOutline, MdTranslate, MdWarningAmber } from 'react-icons/md';
import { useActiveLocales } from '@features/locales/hooks';
import type { Locale } from '@features/locales/schema';
import {
  uiStringFormSchema,
  type UiString,
  type UiStringDetail,
  type UiStringFormTranslation,
  type UiStringFormValues,
} from '../schema';
import { useUiString, useUpdateUiString } from '../hooks';

interface Props {
  open: boolean;
  uiString: UiString | null;
  onClose: () => void;
}

function buildFormTranslations(
  locales: Locale[],
  existing: UiStringDetail['translations'] | undefined,
): UiStringFormTranslation[] {
  const byCode = new Map(existing?.map((t) => [t.localeCode, t]) ?? []);
  return locales
    .filter((l) => !l.isDefault)
    .map((locale) => ({
      localeCode: locale.code,
      localeName: locale.name,
      value: byCode.get(locale.code)?.value ?? '',
    }));
}

function buildEmptyDefaults(
  locales: Locale[],
  existing?: UiStringDetail | null,
): UiStringFormValues {
  return {
    value: existing?.valueEn ?? '',
    description: existing?.description ?? '',
    isActive: existing?.isActive ?? true,
    translations: buildFormTranslations(locales, existing?.translations),
  };
}

export function UiStringFormDialog({ open, uiString, onClose }: Props) {
  const updateMutation = useUpdateUiString();
  const submitting = updateMutation.isPending;

  const { data: locales = [], isLoading: localesLoading } = useActiveLocales();
  const detailQuery = useUiString(uiString?.id ?? null);
  const detail = detailQuery.data ?? null;
  const dataReady = !!uiString && !localesLoading && detailQuery.isSuccess;

  const isUiCoupled =
    (detail?.description ?? '').toLowerCase().includes('ui-coupled');

  const {
    control,
    register,
    handleSubmit,
    reset,
    watch,
    formState: { errors },
  } = useForm<UiStringFormValues>({
    resolver: zodResolver(uiStringFormSchema) as Resolver<UiStringFormValues>,
    defaultValues: useMemo(() => buildEmptyDefaults([]), []),
    mode: 'onSubmit',
    reValidateMode: 'onBlur',
  });

  // Watch the translation rows for rendering (mirrors the other master-data
  // form dialogs). Each row's input is wired with `register` below.
  const translations = watch('translations') ?? [];

  useEffect(() => {
    if (!open || !dataReady) return;
    reset(buildEmptyDefaults(locales, detail));
  }, [open, dataReady, locales, detail, reset]);

  const onSubmit = handleSubmit(async (values) => {
    if (!uiString) return;
    await updateMutation.mutateAsync({ id: uiString.id, values });
    onClose();
  });

  return (
    <Dialog
      open={open}
      onClose={submitting ? undefined : onClose}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>Edit UI string</DialogTitle>
      <DialogContent dividers>
        {!dataReady ? (
          <Box className="flex items-center justify-center py-12">
            <CircularProgress size={28} />
          </Box>
        ) : (
          <form id="ui-string-form" onSubmit={onSubmit} className="grid gap-4 pt-2">
            <TextField
              label="Key"
              size="small"
              fullWidth
              value={uiString?.key ?? ''}
              InputProps={{ readOnly: true }}
              helperText="Key is immutable — defined by the API code; new keys come from trendz-db migrations."
              sx={{
                '& .MuiInputBase-input': {
                  fontFamily: 'ui-monospace, SFMono-Regular, monospace',
                  fontSize: '13px',
                },
              }}
            />

            {isUiCoupled && (
              <Alert
                icon={<MdWarningAmber />}
                severity="warning"
                className="text-[12.5px]"
              >
                <strong>UI-coupled string.</strong> Mobile layout assumes
                specific values (chip widths, axis sizes, etc.). Editing
                without coordinating a mobile release may break rendering.
              </Alert>
            )}

            <TextField
              label="English value (base)"
              size="small"
              fullWidth
              multiline
              minRows={2}
              error={!!errors.value}
              helperText={
                errors.value?.message ??
                'Falls back to this when a locale translation is missing. Placeholders like {percentage}, {qty}, {n} are substituted by mobile at render time.'
              }
              {...register('value')}
            />

            <TextField
              label="Description (ops note)"
              size="small"
              fullWidth
              multiline
              minRows={2}
              error={!!errors.description}
              helperText={
                errors.description?.message ??
                'Where the string is used + any placeholders + UI-coupling notes. Shown to other admins; not surfaced to the mobile app.'
              }
              {...register('description')}
            />

            <Controller
              control={control}
              name="isActive"
              render={({ field }) => (
                <FormControlLabel
                  control={
                    <Switch
                      checked={field.value}
                      onChange={(e) => field.onChange(e.target.checked)}
                    />
                  }
                  label={
                    <span className="text-[13px] text-foreground">
                      {field.value
                        ? 'Active — served to mobile'
                        : 'Inactive — mobile falls back to empty string (kill switch)'}
                    </span>
                  }
                />
              )}
            />

            <div className="grid gap-3">
              <div className="-mb-1 mt-2 flex items-center gap-2 text-[12px] font-semibold uppercase tracking-wider text-foreground-muted">
                <MdTranslate size={14} />
                Translations
                <span className="font-normal normal-case">
                  · leave blank to fall back to English
                </span>
              </div>
              {translations.map((t, i) => {
                const valueErr = errors.translations?.[i]?.value?.message;
                return (
                  <TextField
                    key={t.localeCode}
                    label={`${t.localeName} (${t.localeCode})`}
                    size="small"
                    fullWidth
                    multiline
                    minRows={2}
                    error={!!valueErr}
                    helperText={valueErr ?? ' '}
                    {...register(`translations.${i}.value` as const)}
                  />
                );
              })}
            </div>

            <div className="mt-1 flex items-start gap-2 rounded-lg bg-surface-muted px-3 py-2 text-[12px] text-foreground-muted">
              <MdInfoOutline size={16} className="mt-[1px] shrink-0" />
              <span>
                Changes propagate to mobile within{' '}
                <strong className="text-foreground">60 seconds</strong> (cache
                TTL on the price-detail API). If you don't see the change
                immediately in the app, wait a minute and refresh.
              </span>
            </div>
          </form>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={submitting}>
          Cancel
        </Button>
        <Button
          type="submit"
          form="ui-string-form"
          variant="contained"
          disabled={submitting || !dataReady}
        >
          {submitting ? 'Saving…' : 'Save changes'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
